import { useState, type FormEvent } from 'react'
import { authApi, errorCode } from '@/api/authApi'
import { useAuth } from '@/providers/AuthProvider'
import { startSso } from '@/lib/sso'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { PasswordInput } from '@/components/ui/password-input'

/**
 * Set or reset the signing password of an SSO-only user. The server wants a
 * fresh SSO login for this; REAUTH_REQUIRED sends the user through SSO and back
 * to the page they are on.
 */
export function SigningPasswordForm() {
  const { email } = useAuth()
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [status, setStatus] = useState<{ ok: boolean; msg: string } | null>(null)
  const [busy, setBusy] = useState(false)

  async function submit(e: FormEvent) {
    e.preventDefault()
    e.stopPropagation()
    if (password !== confirm) {
      setStatus({ ok: false, msg: 'Password and confirmation do not match.' })
      return
    }
    setStatus(null)
    setBusy(true)
    try {
      await authApi.setSigningPassword(password)
      setPassword('')
      setConfirm('')
      setStatus({ ok: true, msg: 'Signing password set.' })
    } catch (err) {
      if (errorCode(err) === 'REAUTH_REQUIRED') {
        startSso({ email: email ?? undefined, next: window.location.pathname + window.location.search })
        return
      }
      setStatus({ ok: false, msg: err instanceof Error ? err.message : 'Could not set the signing password' })
    } finally {
      setBusy(false)
    }
  }

  return (
    <form onSubmit={submit} className='flex flex-col gap-4'>
      <div className='flex flex-col gap-1.5'>
        <Label htmlFor='signing-new'>New signing password</Label>
        <PasswordInput
          id='signing-new'
          autoComplete='new-password'
          required
          minLength={8}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
      </div>
      <div className='flex flex-col gap-1.5'>
        <Label htmlFor='signing-confirm'>Confirm signing password</Label>
        <PasswordInput
          id='signing-confirm'
          autoComplete='new-password'
          required
          minLength={8}
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
        />
      </div>
      {status && (
        <p className={status.ok ? 'text-sm text-green-600' : 'text-sm text-destructive'} role='status'>
          {status.msg}
        </p>
      )}
      <Button type='submit' disabled={busy || password.length < 8} className='self-start'>
        {busy ? 'Saving…' : 'Set signing password'}
      </Button>
    </form>
  )
}
